/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import type SimpleBarCore from 'simplebar-core';
import { RichTextEditorHandle } from 'components/RichTextEditor';
import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import useWebSocket from 'utils/useWebSocket';
import { localRandomUuid } from 'utils/string.utils';
import { UserResponse } from 'types';
import { UploadFileApi, UploadS3Response } from 'types/upload-type';

const PAGE_SIZE = 15;

interface CommentsParams {
  withScheduled?: boolean;
  highlightUuid?: string;
  typeUuid: string;
  type: 'tasks' | 'projects' | 'requests';
  onAddComment: (() => void) | undefined;
  onCommentsLoaded: any;
  onFilesDelete?: (uuid: string[]) => void;
  onFinishSubmit?: (values: any, images: UploadFileApi[]) => void;
}

const typeEndpoints: Record<CommentsParams['type'], string> = {
  tasks: apiRoutes.PROJECT_TASKS,
  projects: apiRoutes.PROJECTS,
  requests: apiRoutes.REQUESTS,
};

const typeKeys: Record<CommentsParams['type'], string> = {
  tasks: 'task_id',
  projects: 'project_id',
  requests: 'request_id',
};

export const deletedMessage = '[Comment Deleted]';

const useComments = ({
  highlightUuid,
  typeUuid,
  type,
  onAddComment,
  onCommentsLoaded,
  onFilesDelete,
  onFinishSubmit,
}: CommentsParams) => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const [commentType, setCommentType] = useState('comment');
  const [state, setState] = useState({
    data: [] as Record<string, any>[],
    loading: false,
    current: 1,
    hasMore: true,
    adding: false,
    replyTo: null as any,
    replyToUuid: null as string | null,
    quotedComment: null as any,
    quotedCommentId: null as string | null,
    scrollToTarget: false,
  });
  const [tabView, setTabView] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownResults, setDropdownResults] = useState<any[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const scrollRef = useRef<SimpleBarCore>(null);
  const inputRef = useRef<RichTextEditorHandle>(null);
  const dropdownAbortRef = useRef<AbortController | null>(null);
  const markedThreadReadRef = useRef<string | null>(null);
  const { current, data } = state;
  const endpoint = `${typeEndpoints[type]}/${typeUuid}/comments`;

  const setComment = (text: string) => {
    inputRef.current?.updateValue(text);
  };

  const setQuotedComment = (uuid: string | null, value: any) => {
    setState((prevState) => ({ ...prevState, quotedComment: value, quotedCommentId: uuid, replyTo: null, replyToUuid: null }));
    if (value?.creator && value.creator.uuid !== user.uuid) {
      inputRef.current?.setMentionContents({
        id: value.creator.uuid,
        name: value.creator.name,
      });
    } else {
      inputRef.current?.focus();
    }
  };

  const setReplyTo = (replyToUuid: string | null, value: any, creator?: any) => {
    setState((prevState) => ({ ...prevState, replyTo: value, replyToUuid, quotedComment: null, quotedCommentId: null }));

    if (value?.type === 'private') {
      setCommentType(value.type);
    }

    if (creator) {
      inputRef.current?.setMentionContents({
        id: creator.uuid,
        name: creator.name,
      });
    } else {
      inputRef.current?.focus();
    }

    if (onCommentsLoaded) {
      onCommentsLoaded(false);
    }
  };

  const scrollToBottom = () => {
    const scrollElement = scrollRef.current?.getScrollElement();
    if (scrollElement) {
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  };

  const scrollTo = (offsetTop: any) => {
    const scrollElement = scrollRef.current?.getScrollElement();
    if (scrollElement) {
      scrollElement.scrollTop = offsetTop;
    }
  };

  const handleAddComments = async (values: any = {}, onFinish: any) => {
    try {
      setState((prevState) => ({ ...prevState, adding: true }));

      const res = await apiRequests.post(endpoint, {
        ...values,
        ref_token: localRandomUuid,
      });

      let newComment = res.data.data;
      if (values.quoted_id) {
        const quoted =
          state.data.find((c) => c.uuid === values.quoted_id) ||
          state.data.flatMap((c) => c.replies ?? []).find((r: any) => r.uuid === values.quoted_id);
        if (quoted) newComment = { ...newComment, quoted };
      }

      const allComments = [...state.data, newComment];

      const sortedComments = allComments.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      setState((prevState) => ({
        ...prevState,
        adding: false,
        data: sortedComments,
        current: 1,
        hasMore: prevState.data.length >= PAGE_SIZE,
      }));

      if (onAddComment) {
        onAddComment();
      }

      setTimeout(() => scrollToBottom(), 300);
      onFinish();
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, adding: false }));
    }
  };

  const handleAddReply = async (commentId: string, values: any, onFinish: any) => {
    try {
      setState((prevState) => ({ ...prevState, adding: true }));

      const res = await apiRequests.post(`${apiRoutes.BASE_URL}/projects/comments/${commentId}/comments`, {
        ...values,
        ref_token: localRandomUuid,
      });

      setState((prevState) => {
        const newData = prevState.data.map((el) =>
          el.uuid === commentId
            ? {
                ...el,
                replies: [...el.replies, res.data.data],
              }
            : el,
        );
        return {
          ...prevState,
          adding: false,
          data: newData,
        };
      });

      onFinish();

      setTimeout(() => {
        const scrollEl = scrollRef?.current?.getScrollElement();
        const targetCommentEl = scrollEl?.querySelector<HTMLElement>(`.comment-${commentId}`);
        const targetReplyEl = scrollEl?.querySelector<HTMLElement>(`.comment-${res.data.data.uuid}`);

        if (targetCommentEl && targetReplyEl) {
          const finVal = targetCommentEl.offsetTop + targetCommentEl.scrollHeight - targetReplyEl.scrollHeight;
          scrollTo(finVal);
        }
      }, 300);
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, adding: false }));
    }
  };

  const handleUpdateComment = async (_uuid: any, values: any, uploads: UploadFileApi[], onFinish: any) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      const res = await apiRequests.put(`${apiRoutes.COMMENTS}/${_uuid}`, {
        ...values,
        ref_token: localRandomUuid,
      });

      const updatedComment = res.data.data;

      setState((prevState) => ({
        ...prevState,
        loading: false,
        data: prevState.data.map((el) => (el.uuid === _uuid ? updatedComment : el)),
      }));

      onFinishSubmit?.(
        updatedComment.text,
        uploads.map((file) => {
          return { ...file, response: { ...file.response, stated: true } as UploadS3Response };
        }),
      );
      onFinish();
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleUpdateScheduleTime = async (_uuid: string, value: string, onFinish: any) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      const res = await apiRequests.put(`${apiRoutes.COMMENTS}/${_uuid}`, {
        send_at: value,
        ref_token: localRandomUuid,
      });

      const updatedComment = res.data.data;

      const updatedList = state.data
        .map((c) => (c.uuid === updatedComment.uuid ? updatedComment : c))
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      setState((prevState) => ({
        ...prevState,
        loading: false,
        data: updatedList,
      }));

      onFinish();
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleUpdateReply = async (_uuid: any, commentId: any, values: any, uploads: UploadFileApi[], onFinish: any) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      const res = await apiRequests.put(`${apiRoutes.COMMENTS}/${_uuid}`, {
        ...values,
        ref_token: localRandomUuid,
      });

      const updatedComment = res.data.data;

      setState((prevState) => {
        const newData = prevState.data.map((el) =>
          el.uuid === commentId
            ? { ...el, replies: el.replies.map((reply: any) => (reply.uuid === _uuid ? updatedComment : reply)) }
            : el,
        );

        return {
          ...prevState,
          loading: false,
          data: newData,
        };
      });

      onFinishSubmit?.(
        updatedComment.text,
        uploads.map((file) => {
          return { ...file, response: { ...file.response, stated: true } as UploadS3Response };
        }),
      );

      onFinish();
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleDeleteUpload = async (_uuid: string, commentId: string) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      await apiRequests.delete(`${apiRoutes.UPLOADS}/${_uuid}`);

      setState((prevState) => ({
        ...prevState,
        data: prevState.data.map((el) =>
          el.uuid === commentId ? { ...el, uploads: el.uploads.filter((upload: any) => upload.uuid !== _uuid) } : el,
        ),
        loading: false,
      }));

      onFilesDelete?.([_uuid]);
    } catch (error) {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const getCommentDeleted = (comment: Record<string, any>) => {
    return { ...comment, text: deletedMessage, uploads: [], replies: [], deleted_at: moment().format() };
  };

  const handleDeleteComment = async (_uuid: any) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      await apiRequests.delete(`${apiRoutes.COMMENTS}/${_uuid}`);

      setState((prevState) => ({
        ...prevState,
        loading: false,
        data: prevState.data.map((el) => (el.uuid === _uuid ? getCommentDeleted(el) : el)),
      }));
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleDeleteReply = async (_uuid: any, commentId: any) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));

      await apiRequests.delete(`${apiRoutes.COMMENTS}/${_uuid}`);

      setState((prevState) => {
        const newData = prevState.data.map((el) =>
          el.uuid === commentId
            ? {
                ...el,
                replies: el.replies.map((reply: any) => (reply.uuid === _uuid ? getCommentDeleted(reply) : reply)),
              }
            : el,
        );

        return {
          ...prevState,
          loading: false,
          data: newData,
        };
      });
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleAddReaction = (commentId: string, reaction: any) => {
    setState((prevState) => ({
      ...prevState,
      data: prevState.data.map((comment) => {
        if (comment.uuid !== commentId) return comment;

        const updatedReactions = comment.reactions.some((r: any) => r.uuid === reaction.tempUuid)
          ? comment.reactions.map((r: any) => (r.uuid === reaction.tempUuid ? reaction : r))
          : [...comment.reactions, reaction];

        return {
          ...comment,
          reactions: updatedReactions,
        };
      }),
    }));
  };

  const handleAddReplyReaction = (commentId: string, replyId: string, reaction: any) => {
    setState((prevState) => ({
      ...prevState,
      data: prevState.data.map((comment) => {
        if (comment.uuid !== commentId) return comment;

        return {
          ...comment,
          replies: comment.replies.map((reply: any) => {
            if (reply.uuid !== replyId) return reply;

            const hasOptimistic = reaction.tempUuid ? reply.reactions.some((r: any) => r.uuid === reaction.tempUuid) : false;

            const updatedReactions = hasOptimistic
              ? reply.reactions.map((r: any) => (r.uuid === reaction.tempUuid ? reaction : r))
              : [...reply.reactions, reaction];

            return {
              ...reply,
              reactions: updatedReactions,
            };
          }),
        };
      }),
    }));
  };

  const handleRemoveReplyReaction = (commentId: string, replyId: string, reaction: any) => {
    setState((prevState) => ({
      ...prevState,
      data: prevState.data.map((el) =>
        el.uuid === commentId
          ? {
              ...el,
              replies: el.replies.map((reply: any) =>
                reply.uuid === replyId
                  ? { ...reply, reactions: reply.reactions.filter((item: any) => item.uuid !== reaction.uuid) }
                  : reply,
              ),
            }
          : el,
      ),
    }));
  };

  const handleRemoveReaction = (commentId: string, reaction: any) => {
    setState((prevState) => ({
      ...prevState,
      data: prevState.data.map((el) =>
        el.uuid === commentId ? { ...el, reactions: el.reactions.filter((item: any) => item.uuid !== reaction.uuid) } : el,
      ),
    }));
  };

  // Clears every unread mention the current user has on this thread (top-level comments and
  // replies, archived included), so opening it does not leave stale badges behind. The backend
  // emits the usual `mention.change` / `read` events, so the inbox and the counters catch up on
  // their own. Fire and forget: it never blocks the render and it is safe to repeat.
  const markThreadMentionsAsRead = () => {
    if (type !== 'requests' && type !== 'tasks') return;

    const threadKey = `${type}/${typeUuid}`;
    if (!typeUuid || markedThreadReadRef.current === threadKey) return;
    markedThreadReadRef.current = threadKey;

    apiRequests.post(`${apiRoutes.MENTIONS}/${type}/${typeUuid}/read`).catch(() => {
      // Background housekeeping: stay silent and let the next thread open retry.
      markedThreadReadRef.current = null;
    });
  };

  const fetchComments = async (fnCurrent: number, abort?: AbortController) => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      current: fnCurrent,
    }));

    const params = {
      'page[size]': PAGE_SIZE,
      'page[number]': fnCurrent,
      'sort[by]': 'created_at',
      'sort[direction]': 'desc',
      'filters[type][]': tabView === 'all' ? undefined : tabView,
      with_scheduled: true,
      ...(searchTerm ? { search_term: searchTerm } : {}),
    };

    try {
      const res = await apiRequests.get(endpoint, params, {}, abort);

      const sortedData = (res.data.data as any[]).sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      setState((prevState) => ({
        ...prevState,
        loading: false,
        data: fnCurrent === 1 ? [...sortedData] : [...sortedData, ...prevState.data],
        hasMore: res.data.meta.current_page !== res.data.meta.last_page,
      }));

      if (fnCurrent === 1) {
        markThreadMentionsAsRead();
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const loadMore = () => {
    fetchComments(current + 1);
  };

  const fetchDropdownResults = async (term: string) => {
    if (!term) {
      setDropdownResults([]);
      return;
    }
    dropdownAbortRef.current?.abort();
    dropdownAbortRef.current = new AbortController();
    setDropdownLoading(true);
    try {
      const res = await apiRequests.get(
        endpoint,
        {
          'page[size]': 6,
          'page[number]': 1,
          'sort[by]': 'created_at',
          'sort[direction]': 'desc',
          'filters[type][]': tabView === 'all' ? undefined : tabView,
          with_scheduled: true,
          search_term: term,
        },
        {},
        dropdownAbortRef.current,
      );
      setDropdownResults(res?.data?.data ?? []);
    } catch (e) {
      // aborted or error — silently ignore
    } finally {
      setDropdownLoading(false);
    }
  };

  const commentExistsInData = (dataArray: Record<string, any>[], uuid: string): boolean =>
    dataArray.some((c) => c.uuid === uuid || c.replies?.some((r: any) => r.uuid === uuid));

  const resetComment = () => {
    setState((prevState) => ({ ...prevState, data: [], hasMore: true, scrollToTarget: true }));
  };

  const reloadComment = () => {
    resetComment();
    fetchComments(1);
  };

  const updateReactionsRecursively = (comments: any[], targetUuid: string, newReactions: any[]): any[] => {
    return comments.map((comment) => {
      if (comment.uuid === targetUuid) {
        return {
          ...comment,
          reactions: newReactions,
        };
      }

      if (comment.replies && comment.replies.length > 0) {
        const updatedReplies = updateReactionsRecursively(comment.replies, targetUuid, newReactions);

        if (updatedReplies !== comment.replies) {
          return {
            ...comment,
            replies: updatedReplies,
          };
        }
      }

      return comment;
    });
  };

  useLayoutEffect(() => {
    setState((prevState) => ({ ...prevState, scrollToTarget: true }));
  }, [highlightUuid]);

  useEffect(() => {
    const abort = new AbortController();

    resetComment();
    fetchComments(1, abort);

    return () => {
      abort.abort();
    };
  }, [tabView]);

  useEffect(() => {
    reloadComment();
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      dropdownAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    markedThreadReadRef.current = null;
  }, [type, typeUuid]);

  useEffect(() => {
    if (current === 1 && data.length > 0 && onCommentsLoaded && highlightUuid) {
      const cancel = onCommentsLoaded();

      return () => {
        if (cancel) {
          cancel();
        }
      };
    }

    return () => {};
  }, [data]);

  useEffect(() => {
    if (!highlightUuid || state.loading || !state.hasMore || state.data.length === 0) return;
    if (!commentExistsInData(state.data, highlightUuid)) {
      loadMore();
    }
  }, [state.data, state.loading]);

  useEffect(() => {
    if (!state.scrollToTarget || !state.data.length) {
      return;
    }

    if (highlightUuid) {
      const targetCommentEl = scrollRef.current?.getScrollElement()?.querySelector<HTMLElement>(`.comment-${highlightUuid}`);

      if (targetCommentEl) {
        setState((prev) => ({ ...prev, scrollToTarget: false }));
        const { parentElement } = targetCommentEl;

        scrollTo(
          parentElement?.classList?.contains('ant-comment-nested')
            ? (parentElement.parentElement?.parentElement?.offsetTop ?? 0) + targetCommentEl.offsetTop
            : targetCommentEl.offsetTop,
        );
      }
    } else {
      setState((prev) => ({ ...prev, scrollToTarget: false }));
      scrollToBottom();
    }
  }, [state.scrollToTarget, state.data]);

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.foundation.comment',
      callback: (event: any) => {
        if (
          (state.loading && !state.data.length) ||
          event.payload[typeKeys[type]] !== typeUuid ||
          event.payload.ref_token === localRandomUuid
        )
          return;

        if (type === 'projects' && (event.payload.task_id || event.payload.request_id)) return;

        if (event.action === 'delete') {
          setState((prev) => {
            const newData = prev.data.map((item) => {
              if (event.id === item.uuid) {
                return getCommentDeleted(item);
              }

              return {
                ...item,
                replies: item.replies.map((reply: any) => (reply.uuid === event.id ? getCommentDeleted(reply) : reply)),
              };
            });

            const removeReply = prev.replyToUuid === event.id;

            return {
              ...prev,
              data: newData,
              replyTo: removeReply ? null : prev.replyTo,
              replyToUuid: removeReply ? null : prev.replyToUuid,
            };
          });

          return;
        }

        apiRequests
          .get(`${apiRoutes.COMMENTS}/${event.id}`)
          .catch(() => {})
          .then((response) => {
            if (!response) return;

            const comment = response.data.data;

            if (event.action === 'create') {
              const scrollElement = scrollRef.current?.getScrollElement();
              const shouldScroll =
                scrollElement && scrollElement.scrollTop + scrollElement.offsetHeight >= scrollElement.scrollHeight - 50;

              if (shouldScroll) {
                setTimeout(() => scrollToBottom(), 300);
              }

              setState((prev) => {
                const newData =
                  comment.commentable_type === 'comment'
                    ? prev.data.map((item) => {
                        return comment.commentable_id === item.uuid ? { ...item, replies: [...item.replies, comment] } : item;
                      })
                    : [...prev.data, comment];

                return { ...prev, data: newData };
              });

              return;
            }

            setState((prev) => {
              const newData = prev.data.map((item) => {
                if (comment.commentable_type === 'comment' && comment.commentable_id === item.uuid) {
                  return {
                    ...item,
                    replies: item.replies.map((reply: any) => (reply.uuid === comment.uuid ? comment : reply)),
                  };
                }

                return item.uuid === comment.uuid ? comment : item;
              });

              return { ...prev, data: newData };
            });
          });
      },
    },
  });

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.foundation.upload',
      callback: (event: any) => {
        if (event.action !== 'delete') return;

        const deleteCommentImage = (comment: any) => {
          const newUploads = comment.uploads.filter((upload: any) => upload.uuid !== event.id);

          if (newUploads.length !== comment.uploads.length) {
            comment.uploads = newUploads;
            return true;
          }

          return !!comment.replies.find((reply: any) => deleteCommentImage(reply));
        };

        if (data.find((item) => deleteCommentImage(item))) {
          setState((prev) => ({ ...prev, data: [...data] }));
        }
      },
    },
  });

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.foundation.reaction',
      callback: async (event: any) => {
        const { payload } = event;
        const { reactionable_id: reactionableId } = payload;

        const res = await apiRequests.get(`${apiRoutes.COMMENTS}/${reactionableId}`).catch(() => null);

        if (!res || !res.data?.data) {
          return;
        }

        const updatedCommentOrReply = res.data.data;
        const newReactions = updatedCommentOrReply.reactions;

        setState((prevState) => {
          const updatedData = updateReactionsRecursively(prevState.data, reactionableId, newReactions);

          return {
            ...prevState,
            data: updatedData,
          };
        });
      },
    },
  });

  return {
    state,
    user,
    commentType,
    scrollRef,
    inputRef,
    tabView,
    setTabView,
    searchTerm,
    setSearchTerm,
    dropdownResults,
    dropdownLoading,
    setDropdownResults,
    fetchDropdownResults,
    setCommentType,
    setComment,
    handleAddComments,
    handleUpdateComment,
    handleUpdateScheduleTime,
    handleDeleteComment,
    handleDeleteUpload,
    handleAddReply,
    handleUpdateReply,
    handleDeleteReply,
    handleAddReaction,
    handleRemoveReaction,
    handleAddReplyReaction,
    handleRemoveReplyReaction,
    loadMore,
    setReplyTo,
    setQuotedComment,
    reloadComment,
  };
};

export default useComments;
